import React, { useEffect, useState } from "react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
} from "@dnd-kit/core";
import { Grip } from "../../Icons/icons";
import { FaTimes } from "react-icons/fa"; // Import a cross icon (optional library)

const DroppableBlank = ({ id, word, onClick }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: id.toString(),
  });

  return (
    <div
      ref={setNodeRef}
      className={`relative p-4 w-24 h-12 flex items-center justify-center border-2 border-dashed rounded-lg 
      ${isOver ? "bg-indigo-100 border-indigo-500" : "bg-white border-gray-300"} 
      ${word ? "cursor-pointer" : ""}`}
    >
      {word ? (
        <>
          <span className="text-gray-700 font-semibold">{word}</span>
          {/* Cross icon for undo */}
          <button
            className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center bg-red-500 text-white rounded-full shadow hover:bg-red-600"
            onClick={onClick}
            aria-label="Remove word"
          >
            <FaTimes size={10} />
          </button>
        </>
      ) : (
        <span className="text-gray-400">___</span>
      )}
    </div>
  );
};


const DraggableWord = ({ id, word, onClick }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: id.toString(),
  });

  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      className="px-2 flex items-center bg-indigo-300 text-white font-medium rounded-lg shadow-md cursor-pointer 
      hover:bg-indigo-400 active:shadow-inner"
    >
      <div
        className="text-xs mr-2 h-8 w-2 flex items-center justify-center cursor-move"
        {...attributes}
        {...listeners}
      >
        <Grip />
      </div>
      {word}
    </div>
  );
};


const PreviewSentenceRearranger = ({ data }) => {
  const [state, setState] = useState({ parts: [""], reArrangePart: [""] });
  const [blanks, setBlanks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [sentence,setSentence]=useState("")

  useEffect(() => {
    if (data) {
      setState(data);
      setBlanks(data.reArrangePart.map(() => ""));
      setSentence(data.reArrangePart.join(" "))
    }
  }, [data]);

  const handleDragStart = (event) => {
    if (event?.active?.id) {
      setActiveId(event.active.id);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active?.id) {
      const draggedIndex = parseInt(active.id, 10);
      const overIndex = parseInt(over.id, 10);

      if (isNaN(draggedIndex) || isNaN(overIndex)) return;

      const updatedBlanks = [...blanks];
      updatedBlanks[overIndex] = state.reArrangePart[draggedIndex];
      setBlanks(updatedBlanks);

      const updatedWords = [...state.reArrangePart];
      updatedWords[draggedIndex] = null;
      setState((state) => ({
        ...state,
        reArrangePart: updatedWords,
      }));

      // Check if all blanks are filled and check the answer
      checkAnswer(updatedBlanks);
    }
    setActiveId(null);
  };

  const handleWordClick = (index) => {
    const emptyIndex = blanks.findIndex((blank) => blank === "");

    if (emptyIndex !== -1) {
      const updatedBlanks = [...blanks];
      updatedBlanks[emptyIndex] = state.reArrangePart[index];
      setBlanks(updatedBlanks);

      const updatedWords = [...state.reArrangePart];
      updatedWords[index] = null;
      setState((state) => ({
        ...state,
        reArrangePart: updatedWords,
      }));

      // Check if all blanks are filled and check the answer
      checkAnswer(updatedBlanks);
    }
  };

  const handleClickOnBlank = (index) => {
    const updatedBlanks = [...blanks];
    const removedWord = updatedBlanks[index];
    updatedBlanks[index] = "";
    setBlanks(updatedBlanks);

    if (removedWord) {
      const updatedWords = [...state.reArrangePart];
      const emptyWordIndex = updatedWords.findIndex((word) => word === null);

      if (emptyWordIndex !== -1) {
        updatedWords[emptyWordIndex] = removedWord;
        setState((state) => ({
          ...state,
          reArrangePart: updatedWords,
        }));
      }
    }

    

    // Check if all blanks are filled and check the answer
    checkAnswer(updatedBlanks);
  };

  const checkAnswer = (currentBlanks) => {
    // Check if all blanks are filled
    if (currentBlanks.includes("")) {
      setIsCorrect(null); // Don't check yet if there are still empty blanks
      return;
    }

    const isMatch = currentBlanks.join(" ") === state.parts.join(" ");
    setIsCorrect(isMatch);
  };

  return (
    <div className="p-2 flex flex-col items-center border bg-white shadow-xl rounded">
      <div className="max-w-3xl w-full">
        <h1 className="text-2xl font-extrabold text-gray-800 text-center mb-6">
          Arrange the Sentence
        </h1>

        <h2 className="text-lg font-medium text-gray-600 text-center italic mb-6">
        {sentence && sentence}
        </h2>

        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-700 mb-4">
              Fill in the blanks:
            </h3>
            <div className="flex gap-4 p-4 bg-gray-100 rounded-md shadow-md flex-wrap justify-center min-h-[80px]">
              {blanks.map((word, index) => (
                <DroppableBlank key={index} id={index} word={word} onClick={() => handleClickOnBlank(index)} />
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-700 mb-4">
              Available words:
            </h3>
            <div className="flex gap-4 p-4 mb-8 bg-gray-50 rounded-md shadow-md flex-wrap justify-center">
              {state.reArrangePart.map((word, index) =>
                word ? (
                  <DraggableWord
                    key={index}
                    id={index}
                    word={word}
                    onClick={() => handleWordClick(index)}
                  />
                ) : null
              )}
            </div>
          </div>

          <DragOverlay>
            {activeId !== null && state.reArrangePart[activeId] && (
              <div className="p-2 bg-indigo-400 text-white rounded-md shadow-lg font-semibold">
                {state.reArrangePart[activeId]}
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {isCorrect !== null && (
          <div
            className={`mt-8 p-4 text-center text-lg font-bold rounded-md ${
              isCorrect
                ? "bg-green-100 text-green-700 border border-green-400"
                : "bg-red-100 text-red-700 border border-red-400"
            }`}
          >
            {isCorrect ? "Correct! Well done!" : "Incorrect. Try again!"}
          </div>
        )}
      </div>
    </div>
  );
};

 export default PreviewSentenceRearranger;


